import React, { useState, useEffect } from 'react';
import {
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Typography, Box, Paper, CircularProgress } from '@mui/material';
import { endOfMonth, startOfMonth, addDays, isWithinInterval } from 'date-fns';

const ComplianceGraph = ({ data }) => {
  const [mounted, setMounted] = useState(false);
  const [processedData, setProcessedData] = useState(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (data) {
      // Process the data with correct date calculations
      const now = new Date(2025, 0, 31, 16, 30); // January 31, 2025, 16:30
      const currentPeriodEnd = endOfMonth(now);
      const approachingThreshold = addDays(currentPeriodEnd, -15); // 15 days before end of month

      const chartData = [
        {
          name: 'On Time',
          value: parseInt(data.on_time) || 0,
          fill: '#4caf50' // Green
        },
        {
          name: 'Approaching Deadline',
          value: parseInt(data.approaching) || 0,
          fill: '#ff9800' // Orange/Yellow
        },
        {
          name: 'Overdue',
          value: parseInt(data.overdue) || 0,
          fill: '#f44336' // Red
        }
      ];

      setProcessedData({
        chartData,
        total: parseInt(data.total_users) || 1, // Prevent division by zero
        percentages: {
          onTime: Math.round(((parseInt(data.on_time) || 0) / (parseInt(data.total_users) || 1)) * 100),
          approaching: Math.round(((parseInt(data.approaching) || 0) / (parseInt(data.total_users) || 1)) * 100),
          overdue: Math.round(((parseInt(data.overdue) || 0) / (parseInt(data.total_users) || 1)) * 100)
        }
      });
    }
  }, [data]);

  // Early return if data is not provided or not processed
  if (!data || !processedData) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: 400, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0].payload;
      const percentage = Math.round((value / processedData.total) * 100);
      return (
        <Paper sx={{ p: 1, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <Typography variant="body2">{`${name}: ${value} users`}</Typography>
          <Typography variant="body2">{`${percentage}% of total`}</Typography>
        </Paper>
      );
    }
    return null;
  };

  // Static chart when component is not mounted
  if (!mounted) {
    return (
      <Box sx={{ width: '100%', height: 400, p: 2 }}>
        <Typography variant="h6" align="center" gutterBottom>
          Monthly Checklist Compliance
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h6" align="center" gutterBottom>
        Monthly Checklist Compliance
      </Typography>
      
      <Box sx={{ width: '100%', height: 400, position: 'relative' }}>
        <Box sx={{ position: 'absolute', width: '100%', height: '100%' }}>
          <ResponsiveContainer>
            <BarChart
              data={processedData.chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="value"
                name="Number of Users"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="textSecondary" align="center">
          Total Users: {processedData.total} | On Time: {processedData.percentages.onTime}% | 
          Approaching: {processedData.percentages.approaching}% | Overdue: {processedData.percentages.overdue}%
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" align="center" sx={{ mt: 1 }}>
          * Inspections are due on the last day of each month. Users within 15 days of the deadline are marked as approaching.
        </Typography>
      </Box>
    </Box>
  );
};

export default ComplianceGraph;